/** Операции — то, ради чего приложение и делалось.
 *
 * Чат умеет записать, но листать в нём можно только по одному дню: ни поиска,
 * ни фильтров, ни «куда именно ушло». Здесь всё это появляется впервые.
 */
import { useEffect, useRef, useState } from "react";
import {
  ApiError, api,
  type Failure, type Operation, type Period, type Side, type Span, type Summary,
} from "../lib/api.ts";
import { colorize, isRest, type Colored } from "../lib/category.ts";
import { groupByDay } from "../lib/group.ts";
import { stepSpan, stepTitle } from "../lib/period.ts";
import { formatMoney } from "../lib/money.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { tap } from "../lib/telegram.ts";
import { DailyBars } from "../components/DailyBars.tsx";
import { CategoryIcon } from "../components/CategoryIcon.tsx";
import { Donut } from "../components/Donut.tsx";
import { Fallback } from "./Fallback.tsx";
import { SpanSheet } from "../components/SpanSheet.tsx";
import { Button, Chip, card, hint, listCard } from "../components/ui.tsx";
import { OperationSheet } from "../components/OperationSheet.tsx";
import { Toast } from "../components/Toast.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { onRadioKeys } from "../lib/radiogroup.ts";

const PERIODS: { key: Period; label: string }[] = [
  { key: "day", label: "День" },
  { key: "week", label: "Неделя" },
  { key: "month", label: "Месяц" },
  { key: "year", label: "Год" },
];

export function Operations() {
  const [period, setPeriod] = useState<Period>("month");
  // На сколько периодов назад листнули. Отдельно от `span`: «октябрь» — это всё
  // ещё месяц, и сегмент «Месяц» должен остаться выбранным, иначе человек не
  // поймёт, на что он смотрит.
  const [back, setBack] = useState(0);
  const [span, setSpan] = useState<Span | null>(null);
  const [pickingSpan, setPickingSpan] = useState(false);
  const [chart, setChart] = useState<"donut" | "bars">("donut");
  // Какую сторону разбираем. Отдельного переключателя нет: им работает сама
  // стат-пара — тап по «Доходам» разбирает доходы (паттерн Т-Банка, §2).
  const [side, setSide] = useState<Side>("expense");
  const [query, setQuery] = useState("");
  // Фильтр держим id, а не именем: имя годится для подписи, а сузить ленту на
  // сервере можно только идентификатором.
  const [category, setCategory] = useState<number | null>(null);
  // Состав склейки. Хранится долькой, а не флагом: список категорий уже посчитан
  // в `colorize`, и второй раз его собирать неоткуда — сервер отдаёт всё сразу.
  const [rest, setRest] = useState<Colored | null>(null);
  const [data, setData] = useState<{ summary: Summary; rows: Operation[] } | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [more, setMore] = useState(false);      // идёт подгрузка следующей страницы
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState<Operation | null>(null);
  const [undo, setUndo] = useState<Operation | null>(null);

  // Что на самом деле спрашиваем у сервера: свой диапазон, прошлый период или
  // ничего (тогда сервер берёт текущий сам).
  const range = span ?? (back > 0 ? stepSpan(period, back) : null);

  const load = () => {
    setLoading(true);
    setFailure(null);
    setNext(null);
    Promise.all([
      api.summary(period, range ?? undefined),
      api.operations(period, { q: query, span: range ?? undefined,
                               categoryId: category, side }),
    ])
      .then(([summary, feed]) => { setData({ summary, rows: feed.items }); setNext(feed.next); })
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
  };

  // Категория — тоже условие ленты, а не фильтр загруженного: со страничной
  // лентой отбор на клиенте показывал бы «три траты на еду», когда их тридцать,
  // просто остальные ещё не подгружены.
  useEffect(load, [period, back, query, span, category, side]);

  const loadMore = () => {
    if (!next || more) return;
    setMore(true);
    api.operations(period, { q: query, span: range ?? undefined, categoryId: category,
                             side, cursor: next })
      .then((feed) => {
        setData((prev) => (prev ? { ...prev, rows: [...prev.rows, ...feed.items] } : prev));
        setNext(feed.next);
      })
      .catch(() => setNext(null))   // не догрузилось — просто больше не тянем
      .finally(() => setMore(false));
  };

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const currency = summary?.base_currency ?? "USD";
  // Пирог, легенда и столбики смотрят на выбранную сторону, а не на расходы
  // всегда: доход — такой же разрез, а не приложение к нему.
  const slices = colorize(
    (side === "expense" ? summary?.categories : summary?.income_categories) ?? [],
  );

  return (
    <div style={{ padding: "var(--screen)" }}>
      <h1 style={{ fontSize: "var(--f-t2)", fontWeight: 700, letterSpacing: "-0.3px", margin: 0 }}>
        Операции
      </h1>

      <Search value={query} onChange={setQuery} />
      <SearchHint shown={query.trim() === ""} />
      <Segments active={span ? null : period}
                onChange={(p) => { tap(); setSpan(null); setBack(0); setPeriod(p); }}
                onCustom={() => { tap(); setPickingSpan(true); }} custom={span} />
      {/* Листание периодов. Прошлый месяц стоил шести действий через два
          календаря — а сравнение с прошлым периодом и есть главный вопрос к
          накопленной базе. Вперёд дальше текущего периода не пускаем: там
          пусто по определению. */}
      {!span && (
        <Stepper title={stepTitle(period, back)} back={back}
                 onStep={(d) => { tap(); setBack((n) => Math.max(0, n + d)); }} />
      )}

      {loading || !summary ? (
        <Skeleton />
      ) : summary.tx_count === 0 ? (
        <Empty period={period} span={range} query={query} onClear={() => setQuery("")} />
      ) : (
        <>
          <StatPair summary={summary} side={side}
                    onPick={(next) => { tap(); setSide(next); setCategory(null); }} />
          <section style={{ ...card, marginTop: "var(--s-3)" }}>
            {/* Пончик и столбики отвечают на разные вопросы — «куда» и «когда» —
                и оба сразу занимают полтора экрана до первой строки ленты.
                Переключатель, а не оба (MA-FR-6). */}
            <ChartToggle mode={chart} onChange={setChart} period={period} />
            {slices.length === 0 ? (
              // Кольцо без долек рисуется как «0» в пустом круге — это выглядит
              // поломкой, хотя означает «за период ничего такого не было».
              <p style={{ ...hint, textAlign: "center", margin: "var(--s-6) 0" }}>
                {side === "income"
                  ? "За период не было доходов."
                  : "За период не было трат."}
              </p>
            ) : chart === "donut" ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Donut slices={slices} currency={currency} side={side}
                       total={side === "expense"
                         ? summary.expense_total : summary.income_total} />
              </div>
            ) : (
              <DailyBars summary={summary} side={side} />
            )}
            <Legend
              slices={slices}
              currency={currency}
              active={category}
              onPick={(id) => { tap(); setCategory(category === id ? null : id); }}
              onRest={(slice) => { tap(); setRest(slice); }}
            />
          </section>
          {/* Заголовок нужен именно здесь: выше стоит пирог, подписанный
              «Траты», и без имени список читается как его продолжение — а в нём
              лежат и доходы. Лента остаётся журналом целиком; сузить её может
              только чип категории, и тогда заголовок это и говорит. */}
          <h2 style={{ fontSize: "var(--f-t3)", fontWeight: 600,
                       margin: "var(--s-6) 0 var(--s-3)" }}>
            {category !== null
              ? slices.find((x) => x.category_id === category)?.name ?? "Операции"
              : side === "income" ? "Все доходы" : "Все траты"}
          </h2>
          <Feed rows={rows} currency={currency} slices={slices} onOpen={setOpened}
                hasMore={next !== null} busy={more} onMore={loadMore} />
        </>
      )}

      {rest && (
        <Sheet title={rest.name} onClose={() => setRest(null)}>
          <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                      lineHeight: 1.55, margin: "0 0 var(--s-3)" }}>
            Цветных долек в пироге шесть — дальше глаз их не различает. Эти
            сложены в одну серую; сумма у них общая, а трат по каждой можно
            посмотреть отдельно.
          </p>
          {rest.members!.map((member, i, all) => (
            <button
              key={member.name}
              type="button"
              style={rowButton(i === all.length - 1)}
              disabled={member.category_id === null}
              onClick={() => {
                tap();
                if (member.category_id === null) return;
                setCategory(member.category_id);
                setRest(null);
              }}
            >
              <CategoryIcon name={member.name} color={member.color}
                            icon={member.icon} size={36} />
              <span style={{ flexGrow: 1, minWidth: 0 }}>{member.name}</span>
              <span className="num" style={{ color: "var(--text-muted)" }}>
                {formatMoney(member.amount, currency)}
              </span>
            </button>
          ))}
        </Sheet>
      )}

      {pickingSpan && (
        <SpanSheet current={span} onClose={() => setPickingSpan(false)}
                   onPick={(next) => { setSpan(next); setBack(0); setPickingSpan(false); }}
                   onClear={() => { setSpan(null); setPickingSpan(false); }} />
      )}

      {opened && (
        <OperationSheet
          operation={opened}
          onClose={() => setOpened(null)}
          onChanged={() => { setOpened(null); load(); }}
          onDeleted={(op) => { setOpened(null); setUndo(op); load(); }}
          onRepeated={() => { setOpened(null); load(); }}
        />
      )}

      {/* Удаление без диалога «вы уверены»: одно движение вместо двух, а ошибка
          стоит одного тапа. Данные мягкие — вернуть можно и после тоста. */}
      {undo && (
        <Toast
          text="Запись удалена"
          action="Вернуть"
          // Тост закрываем ТОЛЬКО после ответа: закрыть сразу значит сказать
          // «вернул» до того, как это стало правдой.
          onAction={() => {
            api.restoreOperation(undo.id)
              .then(() => { setUndo(null); load(); })
              .catch(() => setFailure("broken"));
          }}
          onHide={() => setUndo(null)}
        />
      )}
    </div>
  );
}

function Search({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                    background: "var(--surface-2)", borderRadius: "var(--r-field)",
                    padding: "11px 14px", marginTop: "var(--s-4)" }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-faint)"
           strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Найти: название или сумма"
        aria-label="Найти по названию, категории или сумме"
        style={{ flexGrow: 1, background: "none", border: "none",
                 color: "var(--text)", fontSize: "var(--f-body)", font: "inherit", minWidth: 0 }}
      />
    </label>
  );
}

/** Подсказка про сумму. Показываем ТОЛЬКО пока поле пустое: узнать про эту
 *  возможность иначе неоткуда, а висеть над каждым запросом ей незачем. */
function SearchHint({ shown }: { shown: boolean }) {
  if (!shown) return null;
  return (
    <p style={{ fontSize: "var(--f-caption)", color: "var(--text-faint)",
                margin: "var(--s-2) 0 0", lineHeight: 1.5 }}>
      Сумму можно писать как в чате: «300к» найдёт траты около трёхсот тысяч,
      «&gt;5000» — всё дороже пяти тысяч, «100-200» — вилку.
    </p>
  );
}

function Segments({ active, onChange, onCustom, custom }: {
  active: Period | null;                 // null — выбран свой диапазон
  onChange: (p: Period) => void;
  onCustom: () => void;
  custom: Span | null;
}) {
  return (
    <div style={{ display: "flex", gap: "var(--s-2)", marginTop: "var(--s-3)" }}>
    <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Период" style={{ display: "flex",
                                 background: "var(--surface-2)",
                                 borderRadius: "var(--r-pill)", padding: 3, flexGrow: 1 }}>
      {PERIODS.map(({ key, label }) => {
        const on = key === active;
        return (
          <button key={key} type="button" role="radio" aria-checked={on}
            onClick={() => onChange(key)}
            style={{ flexGrow: 1, minHeight: 38, border: "none", cursor: "pointer", font: "inherit",
                     fontSize: "var(--f-caption)", fontWeight: on ? 600 : 400,
                     color: on ? "var(--text)" : "var(--text-muted)",
                     background: on ? "var(--surface-3)" : "transparent",
                     borderRadius: "var(--r-pill)",
                     boxShadow: on ? "var(--shadow-seg)" : "none" }}>
            {label}
          </button>
        );
      })}
    </div>
    {/* «Свой» — не сегмент, а действие: он открывает выбор, а не переключает вид. */}
    <button type="button" onClick={onCustom} aria-pressed={!!custom}
            aria-label={custom ? `Свой период: ${formatSpan(custom)}` : "Свой период"}
            style={{ minHeight: 38, height: 38, alignSelf: "flex-start",
                     padding: "0 var(--pad-control)",
                     borderRadius: "var(--r-pill)",
                     border: "none", cursor: "pointer", font: "inherit",
                     fontSize: "var(--f-caption)", fontWeight: custom ? 600 : 400,
                     background: custom ? "var(--accent)" : "var(--surface-2)",
                     color: custom ? "var(--on-accent)" : "var(--text)" }}>
      {custom ? formatSpan(custom) : "Свой"}
    </button>
    </div>
  );
}

/** Стрелки «назад/вперёд» вокруг подписи периода.
 *
 *  Вперёд с текущего периода не пускаем: будущего в тратах не бывает, а
 *  отключённая стрелка объясняет это лучше пустого экрана. */
function Stepper({ title, back, onStep }: {
  title: string; back: number; onStep: (delta: number) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)",
                  marginTop: "var(--s-3)" }}>
      <StepButton label="Предыдущий период" onClick={() => onStep(1)}>‹</StepButton>
      <div aria-live="polite" style={{ flexGrow: 1, textAlign: "center",
                                       fontSize: "var(--f-body)", fontWeight: 600 }}>
        {title}
      </div>
      <StepButton label="Следующий период" disabled={back === 0}
                  onClick={() => onStep(-1)}>›</StepButton>
    </div>
  );
}

function StepButton({ label, disabled, onClick, children }: {
  label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button type="button" aria-label={label} disabled={disabled} onClick={onClick}
            style={{ width: 44, minHeight: 38, border: "none", borderRadius: "var(--r-pill)",
                     cursor: disabled ? "default" : "pointer", font: "inherit",
                     fontSize: "var(--f-t3)", lineHeight: 1,
                     background: "var(--surface-2)",
                     color: disabled ? "var(--text-disabled)" : "var(--text)" }}>
      {children}
    </button>
  );
}

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн",
                      "июл", "авг", "сен", "окт", "ноя", "дек"];

function formatSpan(span: Span): string {
  const day = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${Number(d)} ${MONTHS_SHORT[Number(m) - 1]}`;
  };
  return span.from === span.to ? day(span.from) : `${day(span.from)}\u2009—\u2009${day(span.to)}`;
}


/** Пара стат-карточек. Трат здесь НЕТ намеренно: они и так стоят в центре пирога,
 *  а одно и то же число дважды на экране — шум. Вместо дубля показываем «Чистый»:
 *  доходы минус расходы (FR-30) — единственное число, отвечающее на «месяц в плюсе?». */
/** Траты и доходы за период, под каждым — состав (MA-FR-5).
 *
 * Пара именно траты/доходы, а не доходы/чистый: сумма трат раньше жила только в
 * центре пончика, и после появления переключателя графиков на «по дням» она
 * пропадала с экрана совсем.
 *
 * Мини-бар отвечает на «из чего сложилось» до того, как человек полез в пирог.
 * Чистый ушёл подписью под парой: он производный от обоих чисел, и своя карточка
 * ставила бы его вровень с ними.
 */
function StatPair({ summary, side, onPick }: {
  summary: Summary; side: Side; onPick: (side: Side) => void;
}) {
  const c = summary.base_currency;
  const positive = !summary.net.startsWith("-");
  return (
    <>
      <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Что разбираем"
           style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
                    marginTop: "var(--s-3)" }}>
        <StatCard label="Траты" amount={summary.expense_total} currency={c}
                  parts={colorize(summary.categories)} active={side === "expense"}
                  onPick={() => onPick("expense")} />
        <StatCard label="Доходы" amount={summary.income_total} currency={c}
                  ink="var(--income)" parts={colorize(summary.income_categories)}
                  active={side === "income"} onPick={() => onPick("income")} />
      </div>
      <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                  margin: "var(--s-2) 0 0", textAlign: "center" }}>
        Чистый{" "}
        <span className="num" style={{ color: positive ? "var(--income)" : "var(--text)" }}>
          {positive ? "+" : ""}{formatMoney(summary.net, c)}
        </span>
      </p>
    </>
  );
}

/** Карточка стат-пары — она же переключатель (§2, паттерн Т-Банка).
 *
 *  Отдельного контрола «Траты / Доходы» нет намеренно: обе карточки уже стоят
 *  рядом и уже названы, а вторая полоса сегментов над третьей (период и вид
 *  графика уже есть) съела бы экран до первой строки ленты.
 *
 *  Активную отмечаем рамкой И яркостью подписи — цвет один в этом наборе, и
 *  различать состояния только им нельзя (WCAG 1.4.1).
 */
function StatCard({ label, amount, currency, parts, active, onPick, ink }: {
  label: string; amount: string; currency: string; parts: Colored[];
  active: boolean; onPick: () => void; ink?: string;
}) {
  return (
    <button type="button" role="radio" aria-checked={active} onClick={onPick}
      style={{ ...card, textAlign: "left", cursor: "pointer", font: "inherit",
               color: "inherit", padding: "var(--s-4)",
               border: active ? "1.5px solid var(--accent)" : "1.5px solid transparent" }}>
      <div className="num" style={{ ...stat, color: ink ?? "var(--text)" }}>
        {formatMoney(amount, currency)}
      </div>
      <div style={{ ...statLabel, color: active ? "var(--text)" : "var(--text-muted)" }}>
        {label}
      </div>
      <Composition parts={parts} label={`Состав ${label === "Доходы" ? "доходов" : "трат"}`} />
    </button>
  );
}

/** Полоска состава: доли теми же цветами, что в пироге.
 *
 * Ширина — единственное место, где доля становится числом, и это проценты, а не
 * деньги: складывать копейки во фронте нечем (MA-INV-6).
 */
function Composition({ parts, label }: { parts: Colored[]; label: string }) {
  const shown = parts.filter((p) => p.share > 0);
  if (shown.length === 0) return null;
  return (
    <div role="img" aria-label={`${label}: ${shown.map((p) => `${p.name} ${p.share}%`).join(", ")}`}
         style={{ display: "flex", gap: 2, height: 5, marginTop: 9, overflow: "hidden" }}>
      {shown.map((p) => (
        <span key={p.name} style={{ flexGrow: p.share, background: p.color,
                                    borderRadius: "var(--r-bar)", minWidth: 2 }} />
      ))}
    </div>
  );
}

/** Пончик или столбики. По умолчанию пончик: «куда ушло» — вопрос, ради
 *  которого экран и открывают; «когда» спрашивают реже. */
function ChartToggle({ mode, onChange, period }: {
  mode: "donut" | "bars"; onChange: (m: "donut" | "bars") => void; period: Period;
}) {
  // На годе ряд склеен в месяцы: обещать «по дням» и показать столбик за август
  // — это разные вещи, и подпись обязана говорить правду.
  const options = [
    ["donut", "По категориям"],
    ["bars", period === "year" ? "По месяцам" : "По дням"],
  ] as const;
  return (
    <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Вид графика"
         style={{ display: "flex", gap: "var(--s-2)", marginBottom: "var(--s-4)" }}>
      {options.map(([key, label]) => {
        const on = key === mode;
        return (
          <button key={key} type="button" role="radio" aria-checked={on}
                  onClick={() => { tap(); onChange(key); }}
                  style={{ flexGrow: 1, minHeight: 38, border: "none", cursor: "pointer",
                           font: "inherit", fontSize: "var(--f-caption)",
                           fontWeight: on ? 600 : 400,
                           borderRadius: "var(--r-pill)",
                           background: on ? "var(--accent-tint)" : "var(--surface-2)",
                           color: on ? "var(--accent)" : "var(--text-muted)" }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Legend({ slices, currency, active, onPick, onRest }: {
  slices: Colored[]; currency: string;
  active: number | null; onPick: (categoryId: number) => void;
  onRest: (slice: Colored) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)", marginTop: "var(--s-4)" }}>
      {slices.map((slice) => {
        const on = slice.category_id !== null && slice.category_id === active;
        return (
          // Склейка сузить ленту не может — своего id у неё нет, — но состав
          // показать может, и нажатие открывает именно его. Долька без id и без
          // состава — категория, которой уже нет: вот она и правда не нажимается.
          <Chip key={slice.name} active={on} ink={slice.color}
                disabled={!isRest(slice) && slice.category_id === null}
                onPick={() => {
                  if (isRest(slice)) return onRest(slice);
                  if (slice.category_id !== null) onPick(slice.category_id);
                }}>
            {slice.name}
            <span className="num" style={{ color: on ? "inherit" : "var(--text-muted)" }}>
              {formatMoney(slice.amount, currency)}
            </span>
          </Chip>
        );
      })}
    </div>
  );
}

function Feed({ rows, currency, slices, onOpen, hasMore, busy, onMore }: {
  rows: Operation[]; currency: string; slices: Colored[]; onOpen: (op: Operation) => void;
  hasMore: boolean; busy: boolean; onMore: () => void;
}) {
  const colorOf = (name: string) =>
    slices.find((s) => s.name === name)?.color ?? "var(--cat-rest)";
  if (rows.length === 0) {
    return (
      <p style={{ ...hint, marginTop: "var(--s-6)" }}>
        В этой категории за период ничего нет. Нажми чип ещё раз, чтобы увидеть всё.
      </p>
    );
  }
  return (
    <>
      {groupByDay(rows).map((group) => (
        <section key={group.day} style={{ marginTop: "var(--s-6)" }}>
          <header style={{ display: "flex", justifyContent: "space-between",
                           alignItems: "baseline", marginBottom: "var(--s-3)" }}>
            <h2 style={{ fontSize: "var(--f-t3)", fontWeight: 600, margin: 0 }}>{group.label}</h2>
            <span className="num" style={{ fontSize: "var(--f-body-s)",
                    color: group.total.startsWith("-") ? "var(--text-muted)" : "var(--income)" }}>
              {/* Плюс обязателен: итог дня — это доход минус расход, и зелёное
                  «211,2 $» без знака читается как «потратил», а не «заработал».
                  В строках операций плюс уже стоит, в шапке дня его не было. */}
              {group.total.startsWith("-") ? "" : "+"}
              {formatMoney(group.total, currency)}
            </span>
          </header>
          <div style={listCard}>
            {group.rows.map((row, i) => (
              <Row key={row.id} row={row} currency={currency} onOpen={onOpen}
                 color={colorOf(row.category_name)} last={i === group.rows.length - 1} />
            ))}
          </div>
        </section>
      ))}
      {hasMore && <More busy={busy} onReach={onMore} />}
    </>
  );
}

/** Подгрузка следующей страницы, когда до неё домотали.
 *
 * Наблюдатель, а не кнопка «ещё»: лента листается пальцем, и лишнее нажатие в
 * середине движения — это остановка. Кнопка всё же есть — она же и якорь
 * наблюдателя: без клавиатурного пути до следующей страницы не добраться вовсе.
 */
function More({ busy, onReach }: { busy: boolean; onReach: () => void }) {
  const anchor = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const node = anchor.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    // rootMargin вперёд: начинаем тянуть до того, как якорь показался, иначе
    // человек упирается в пустоту и ждёт.
    const eye = new IntersectionObserver(
      (entries) => entries.some((e) => e.isIntersecting) && onReach(),
      { rootMargin: "400px" },
    );
    eye.observe(node);
    return () => eye.disconnect();
  }, [onReach]);

  return (
    <button ref={anchor} type="button" onClick={onReach} disabled={busy}
            style={{ display: "block", width: "100%", marginTop: "var(--s-5)", padding: 14,
                     background: "none", border: "none", cursor: "pointer", font: "inherit",
                     fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
      {busy ? "Гружу…" : "Показать ещё"}
    </button>
  );
}

function Row({ row, currency, color, last, onOpen }: {
  row: Operation; currency: string; color: string; last: boolean;
  onOpen: (op: Operation) => void;
}) {
  const income = row.op_type === "income";
  // Оригинальную сумму показываем, только если платил не в базовой: «34 $ / 34 $»
  // — шум, а «34 $ / 900 000 ₫» — та самая честность чисел.
  const original = row.currency !== row.base_currency;
  return (
    <button type="button" onClick={() => { tap(); onOpen(row); }}
      style={rowButton(last)}>
      <CategoryIcon name={row.category_name} color={color} />
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div style={{ fontSize: "var(--f-body)", fontWeight: 500, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.description || row.category_name}
        </div>
        <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 1 }}>
          {row.category_name}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div className="num" style={{ fontSize: "var(--f-body)", fontWeight: 500,
                                      color: income ? "var(--income)" : "var(--text)" }}>
          {income ? "+" : "−"}{formatMoney(row.amount_base, currency)}
        </div>
        {original && (
          <div className="num" style={{ fontSize: "var(--f-caption-s)", color: "var(--text-muted)" }}>
            {formatMoney(row.amount, row.currency)}
          </div>
        )}
      </div>
    </button>
  );
}

function Empty({ period, span, query, onClear }: {
  period: Period; span: Span | null; query: string; onClear: () => void;
}) {
  // Винительный падеж, а не именительный из сегментов: «За неделя записей ещё
  // нет» — то, что получалось при подстановке подписи кнопки. А при выбранном
  // своём диапазоне `period` остаётся прежним, и текст называл период, которого
  // человек не выбирал: «За месяц» на диапазоне «12—27 августа».
  const named = span ? formatSpan(span)
    : { day: "день", week: "неделю", month: "месяц", year: "год" }[period] ?? "период";
  return (
    <div style={{ ...card, marginTop: "var(--s-3)", textAlign: "center", padding: "var(--s-8)" }}>
      <p style={{ fontSize: "var(--f-body)", fontWeight: 600, margin: 0 }}>
        {query ? "Ничего не нашлось" : "Пока пусто"}
      </p>
      <p style={{ ...hint, marginTop: "var(--s-2)" }}>
        {query
          ? `За ${named} по запросу «${query}» записей нет.`
          : `За ${named} записей ещё нет. Скажи боту «кофе 80к» — появится здесь.`}
      </p>
      {query && (
        <Button tone="quiet" onPick={onClear} style={{ marginTop: "var(--s-4)" }}>
          Очистить поиск
        </Button>
      )}
    </div>
  );
}

/** Скелетон повторяет ФОРМУ экрана, а не одну его деталь.
 *
 *  Раньше здесь был только круг пончика — а появлялись пара стат-карточек,
 *  переключатель, пончик, легенда, заголовок и лента. Экран прыгал на пол-высоты
 *  ровно в тот момент, когда человек начинал в него смотреть. */
function Skeleton() {
  const bar = (w: string, h: number, mt = 0) => (
    <div style={{ width: w, height: h, marginTop: mt, borderRadius: "var(--r-sm)",
                  background: "var(--surface-2)" }} />
  );
  return (
    <div role="status" aria-busy="true" aria-label="Загружаю операции">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
                    marginTop: "var(--s-3)" }}>
        {[0, 1].map((i) => (
          <div key={i} style={card}>
            {bar("62%", 26)}
            {bar("38%", 11, 8)}
            {bar("100%", 5, 9)}
          </div>
        ))}
      </div>
      <div style={{ ...card, marginTop: "var(--s-3)" }}>
        {bar("100%", 36)}
        <div style={{ width: 200, height: 200, borderRadius: "50%", margin: "var(--s-4) auto 0",
                      border: "26px solid var(--surface-2)" }} />
        {bar("70%", 34, 16)}
      </div>
      {bar("34%", 17, 24)}
      <div style={{ ...card, marginTop: "var(--s-3)" }}>
        {[0, 1, 2].map((i) => bar("100%", 40, i ? 8 : 0))}
      </div>
    </div>
  );
}



const stat: React.CSSProperties = {
  fontSize: "var(--f-t2)",
  fontWeight: 700,
  letterSpacing: "-0.4px",
};

const statLabel: React.CSSProperties = {
  fontSize: "var(--f-caption)",
  color: "var(--text-muted)",
  marginTop: 3,
};


